import i18next from 'i18next';
import _ from 'lodash';
import { setShowModal } from '@/core/hooks/useModalManager.hook';
import { ASSET_PATH_SEPARATOR } from '@/main/app.constants';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { sqHierarchiesApi } from '@/sdk/api/HierarchiesApi';
import { sqItemsApi } from '@/sdk/api/ItemsApi';
import { AssetReplaceModal } from '@/search/AssetReplaceModal.molecule';
import { MAX_SWAP_OPTION_NAME_LENGTH } from '@/search/search.constants';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { changeAssetId } from '@/tableBuilder/tableBuilder.actions';
import { ID_COLUMN_TYPES } from '@/tableDefinitionEditor/tableDefinition.constants';
import { getAllItems } from '@/trend/trendDataHelper.utilities';
import { swapAssets, swapScalingTableRows } from '@/trendData/trend.actions';
import { notifyWarning } from '@/utilities/screenshot.utilities';
import { warnToast } from '@/utilities/toast.utilities';
import type { HierarchyNodeOutputV1 } from '@/sdk/model/HierarchyNodeOutputV1';
import type { SwapOptionListV1, SwapOptionV1 } from '@/sdk/model/models';
import type { AssetReplaceModalProps } from '@/search/AssetReplaceModal.molecule';
import type { ScalingTableSwapRow, SelectedSearchItem } from '@/search/search.types';

export function getAutoSwap(swapOptionList: SwapOptionListV1, swapInId: string): SwapOptionV1 | undefined {
  if (swapOptionList.swapOptions && swapsPerfectly(swapOptionList.swapOptions)) {
    return swapOptionList.swapOptions![0];
  }

  // It may be that there is more than one option, but that once we exclude identity swaps (swapping the same
  // asset that is already in the trend) which we can assume the user doesn't want, there is only one left.
  const swapOptionsWithoutIdentity = _.reject(
    swapOptionList.swapOptions,
    (option) => option.swapRootCandidate?.id === swapInId,
  );
  if (swapsPerfectly(swapOptionsWithoutIdentity)) {
    return swapOptionsWithoutIdentity[0];
  }
}

/**
 * A swap can be done without user input if it is the only one in the list and all parameters of the formula
 * item can be swapped.
 */
export function swapsPerfectly(swapOptions: SwapOptionV1[]): boolean {
  return swapOptions.length === 1;
}

export function getSwapPairs(swapOption: SwapOptionV1) {
  return _.mapValues(_.keyBy(swapOption.itemsWithSwapPairs, 'item.id'), 'swapPairs');
}

export function getSwapRootChoices(
  swapOptionList: SwapOptionListV1,
  itemId: string,
  defaultItems:
    | { assets: { id: string; pathComponentIds: string[]; pathComponentDisabled: boolean }[] }[]
    | undefined = undefined,
) {
  const startingAssets = _.flatMap(defaultItems ? defaultItems : getAllItems({}), 'assets');
  // The path length is one more than the ancestor count since we count the item itself
  const validSwapOptionIds = _.chain(swapOptionList.swapOptions)
    .filter((option) => !_.isEmpty(option.itemsWithSwapPairs))
    .map('swapRootCandidate.id')
    .value();
  // Available assets are the immediate parents of the items or their parameters
  const availableAssets = _.chain(startingAssets)
    .reject(['id', itemId])
    .map((asset) =>
      _.assign(
        {
          pathComponentDisabled: _.map(asset.pathComponentIds, (id) => _.indexOf(validSwapOptionIds, id) === -1),
        },
        asset,
      ),
    )
    .reject((asset) => _.every(asset.pathComponentDisabled))
    .sortBy(['formattedName'])
    .uniqBy('id')
    .value();
  const likelySwapOuts = _.chain(availableAssets)
    .map((asset) => {
      // The likely swap-out is the path component that ranks highest in the pre-sorted swapOptions
      const id: string = _.head(
        _.sortBy(asset.pathComponentIds, (id) =>
          _.indexOf(validSwapOptionIds, id) === -1 ? Infinity : _.indexOf(validSwapOptionIds, id),
        ),
      );
      const name: string | undefined = _.nth(
        _.split(asset.formattedName, ASSET_PATH_SEPARATOR),
        _.indexOf(asset.pathComponentIds, id),
      );
      const disabled: boolean = asset.pathComponentDisabled[_.indexOf(asset.pathComponentIds, id)];
      return { id, name, disabled };
    })
    .uniqBy('id')
    .value();
  return { availableAssets, likelySwapOuts };
}

export function swapAsset(
  asset: { id: string; ancestors?: any[]; name?: string },
  swapAssetsFn = swapAssets,
  defaultItems:
    | { assets: { id: string; pathComponentIds: string[]; pathComponentDisabled: boolean }[] }[]
    | undefined = undefined,
) {
  // Do not swap assets fed from Scaling Table rows
  const swapOutItemIds = _.chain(getAllItems({}))
    .uniqBy('id')
    .filter((item) => !('rowName' in item && 'columnName' in item))
    .map('id')
    .value();

  return sqItemsApi.getSwapOptions({ id: asset.id, swapOutItemIds }).then(({ data: swapOptionList }) => {
    const autoSwap = getAutoSwap(swapOptionList, asset.id);
    if (autoSwap) {
      if (!_.isEmpty(asset.ancestors)) {
        changeAssetId((_.last(asset.ancestors) as any).id);
      }
      const invalidSwapOuts = autoSwap.invalidSwapOuts;
      if (invalidSwapOuts && invalidSwapOuts.length > 0) {
        const invalidItemNames = _.map(invalidSwapOuts, (s) => s.item?.name).join(', ');
        notifyWarning(i18next.t('ASSET_REPLACE.INVALID', { NAMES: invalidItemNames }));
      }
      const swapPairs = getSwapPairs(autoSwap);

      return swapAssetsFn(swapPairs);
    } else if (swapOptionList.swapOptions && swapOptionList.swapOptions.length > 0) {
      if (headlessRenderMode()) {
        notifyWarning(i18next.t('ASSET_REPLACE.MULTI_SWAP_COMING_LATER'));
        return Promise.reject('Multi-asset swap not supported');
      } else {
        const { availableAssets, likelySwapOuts } = getSwapRootChoices(swapOptionList, asset.id, defaultItems);
        const modalSize = _.max(_.map(availableAssets, 'formattedName.length')) > 50 ? 'lg' : 'sm';
        const swapOptions = swapOptionList.swapOptions;
        setShowModal(AssetReplaceModal, true, {
          selectedAsset: asset,
          availableAssets,
          likelySwapOuts,
          onAssetSelection: ({ id }) => {
            const swapOption = swapOptions.find((option) => option.swapRootCandidate?.id === id);
            if (!swapOption) {
              return Promise.resolve();
            }
            return swapAssets(getSwapPairs(swapOption)).catch(_.noop);
          },
          size: modalSize,
          onClose: () => setShowModal(AssetReplaceModal, false),
        } as AssetReplaceModalProps);
      }
    } else if (swapOutItemIds.length) {
      if (headlessRenderMode()) {
        // TODO: CRAB-30040: Change how this warning is handled.
        notifyWarning(i18next.t('ASSET_REPLACE.INVALID_ALL'));
      } else {
        warnToast({ messageKey: 'ASSET_REPLACE.NOT_POSSIBLE' });
      }
    }
  });
}
/**
 * Swaps all items in the details pane with the same source row name as the selected row item. Only items from a
 * scaling table that originate from the same scaling table will be swapped. Scaling items are represented in the
 * details pane by the source row/column/table name metadata associated with the item.
 *
 * @param rowItem - the scaling table row item to swap all items with the same source row name
 * @returns {Promise} Promise that resolves when the swap is complete
 */
export async function swapScalingTableRow(rowItem: SelectedSearchItem) {
  const sourceTableDefinition = rowItem.ancestors?.find(
    (ancestor) => ancestor.type === SeeqNames.Types.TableDefinition,
  );
  const allItems = getAllItems({ excludeDummyItems: false });
  const allScalingTableItems = _.chain(allItems)
    .uniqBy('id')
    .filter((item) => 'rowName' in item && 'columnName' in item)
    .value();

  const swapOutItems = allScalingTableItems
    .filter((item) => item.tableId === sourceTableDefinition?.id)
    .map((item) => ({
      id: item.id,
      name: item.name,
      rowName: item.rowName,
      rowId: item.rowId ?? item.id,
      columnName: item.columnName,
      tableName: item.tableName,
      tableId: item.tableId,
      datumId: item.datumId,
    })) as ScalingTableSwapRow[];

  const uniqueRows = _.uniqBy(
    swapOutItems
      .map((item) => ({ name: item.rowName, id: item.rowId ?? item.id }))
      .filter((item) => item.id !== rowItem.id),
    (row) => `${row.name}|${row.id}`,
  );
  const swapRow = { id: rowItem.id, name: rowItem.name ?? '' };

  if (allScalingTableItems.length === 0) {
    rowSwapWarning('SCALING_ROW_REPLACE.INVALID_ALL', 'SCALING_ROW_REPLACE.NO_ITEM_SELECTED');
    return;
  }

  if (uniqueRows.length === 0) {
    // If there are no rows for the scaling table in the details pane, we cannot swap
    rowSwapWarning('SCALING_ROW_REPLACE.INVALID_ALL', 'SCALING_ROW_REPLACE.NOT_POSSIBLE');
    return;
  }

  if (uniqueRows.length === 1) {
    // Swap all the items with the same row name
    const swapItems = swapOutItems.filter((item) => item.rowId === uniqueRows[0].id);
    const remainingSwapItems = warnOfExistingScalingTableSwapItems(swapItems, swapOutItems, rowItem);
    if (remainingSwapItems.length > 0) {
      swapScalingTableRows(swapRow, remainingSwapItems, allItems.length);
    }
  } else if (uniqueRows.length > 1) {
    // If there are multiple rows, we need to prompt the user to select which row to swap
    const modalSize = _.max(_.map(uniqueRows, 'length')) > MAX_SWAP_OPTION_NAME_LENGTH ? 'lg' : 'sm';
    setShowModal(AssetReplaceModal, true, {
      selectedAsset: swapRow,
      availableAssets: [],
      likelySwapOuts: uniqueRows.map((item) => ({
        id: item.id,
        name: item.name,
        disabled: false,
      })),
      onAssetSelection: (option) => {
        const swapItems = swapOutItems.filter((item) => item.rowId === option.id);
        const remainingSwapItems = warnOfExistingScalingTableSwapItems(swapItems, swapOutItems, rowItem);
        if (remainingSwapItems.length > 0) {
          return swapScalingTableRows(swapRow, remainingSwapItems, allItems.length);
        }
      },
      size: modalSize,
      onClose: () => setShowModal(AssetReplaceModal, false),
      isSwappingScalingTableRow: true,
    } as AssetReplaceModalProps);
  }
}

/**
 * Warns the user if there are existing scaling table swap items that exist and should not swap those items.
 * @param swapItems - the items that are potentially being swapped
 * @param allSwapItems - all items that come from a scaling table and can be swapped
 * @param swapRow - the row with items being swapped
 */
const warnOfExistingScalingTableSwapItems = (
  swapItems: ScalingTableSwapRow[],
  allSwapItems: ScalingTableSwapRow[],
  swapRow: SelectedSearchItem,
) => {
  const [matchedItems, remainingItems] = _.partition(
    swapItems,
    (swapItem) =>
      (swapRow.type === SeeqNames.Types.Asset && ID_COLUMN_TYPES.includes(swapItem.columnName)) ||
      _.some(allSwapItems, (allItem) => allItem.columnName === swapItem.columnName && allItem.rowId === swapRow.id),
  );

  if (remainingItems.length === 0) {
    rowSwapWarning('SCALING_ROW_REPLACE.INVALID_ALL', 'SCALING_ROW_REPLACE.NOT_POSSIBLE');
  } else if (matchedItems.length > 0) {
    const names = _.map(matchedItems, (s) => s.name).join(', ');
    if (headlessRenderMode()) {
      notifyWarning(i18next.t('SCALING_ROW_REPLACE.INVALID', { NAMES: names }));
    } else {
      warnToast({
        messageKey: 'SWAPS.INVALID_SWAPS_V2',
        messageParams: {
          invalidSwapOutItemNames: names,
        },
      });
    }
  }

  return remainingItems;
};

const rowSwapWarning = (headlessMessageKey: string, messageKey: string) => {
  if (headlessRenderMode()) {
    notifyWarning(i18next.t(headlessMessageKey));
  } else {
    warnToast({ messageKey });
  }
};

export const getScalingTableItemTrendProps = async (item: SelectedSearchItem) => {
  if (item.ancestors?.some((ancestor) => ancestor.type === SeeqNames.Types.TableDefinition)) {
    const scalingTable = item.ancestors!.find((ancestor) => ancestor.type === SeeqNames.Types.TableDefinition);
    try {
      const { data: hierarchyInfo } = await sqHierarchiesApi.searchHierarchyNode({
        scalingTableId: scalingTable!.id,
        itemId: item.id,
        rowItemId: item.ancestors[1]?.id ?? item.id,
      });
      return {
        tableId: scalingTable!.id,
        rowId: item.ancestors[1]?.id ?? item.id,
        columnId: item.columnId,
        hierarchyId: hierarchyInfo?.hierarchyId,
        hierarchyName: hierarchyInfo?.hierarchyName,
        nodePath: hierarchyInfo?.path,
        alias: hierarchyInfo?.node?.displayName ?? item.name,
        datumId: item.ancestors[1]?.datumId,
        instanceId: item.properties?.find((i) => i.name === SeeqNames.Properties.CreatedByModelInstance)?.value,
      };
    } catch (error) {
      return {
        tableId: scalingTable!.id,
        rowId: item.ancestors[1]?.id ?? item.id,
        columnId: item.columnId,
        datumId: item.ancestors[1]?.datumId,
        instanceId: item.properties?.find((i) => i.name === SeeqNames.Properties.CreatedByModelInstance)?.value,
      };
    }
  }
  return undefined;
};

export const getHierarchyItemTrendProps = (item: SelectedSearchItem, nodePath: HierarchyNodeOutputV1[]) => {
  if (item.ancestors?.some((ancestor) => ancestor.type === SeeqNames.Types.Hierarchy)) {
    const hierarchy = item.ancestors!.find((ancestor) => ancestor.type === SeeqNames.Types.Hierarchy);
    if (!hierarchy) {
      return undefined;
    }
    return {
      hierarchyId: hierarchy.id,
      hierarchyName: hierarchy.name,
      nodePath,
      alias: item.node?.displayName ?? item.name,
    };
  }
};
